import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import withAuth from '../../auth';
import Spinner from 'react-bootstrap/Spinner';
import NPMapViewDirections from '../google_map/NPMapViewDirections';
import { GoogleMapsProvider } from '../google_map/GoogleMapsContext';
import moment from 'moment';
import { handleApiRequest } from '../../utils/apiHandler';
import api from '../../services/axios';
import useNotification from '../../hooks/useNotification';
import PriceEstimate from './pickup-create/PriceEstimate';
import DeliveryOptions from './pickup-create/DeliveryOptions';
import ItemSection from './pickup-create/ItemSection';
import RouteSection from './pickup-create/RouteSection';

const PickupCreate = () => {
  const { userId } = useParams();
  const { showError, showSuccess } = useNotification();
  const [user, setUser] = useState(null);
  const [routes, setRoutes] = useState([
    {
      id: 1,
      origin: {},
      dropoff: {},
      originUseMyself: false,
      dropoffUseMyself: false,
    },
  ]);
  const [distance, setDistance] = useState(0);

  const [additionalInfo, setAdditionalInfo] = useState('');
  const additionalInfoRef = useRef(null);

  const [truckType, setTruckType] = useState(12);
  const [truckTypeList, setTruckTypeList] = useState([]);
  const [cargoInsurance, setCargoInsurance] = useState(6);
  const [items, setItems] = useState([]);
  const [pickup, setPickup] = useState({ origins: [], dropoffs: [] });
  const [optimizedWaypoints, setOptimizedWaypoints] = useState([]);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledPickupTime, setScheduledPickupTime] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const itemIdCounter = useRef(0);
  const itemDetailsRefs = useRef({});

  const [priceEstimate, setPriceEstimate] = useState(null);
  const [isEstimating, setIsEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const duplicatePickupId = new URLSearchParams(window.location.search).get(
    'duplicate'
  );

  useEffect(() => {
    if (duplicatePickupId) {
      fetchExistingPickup(duplicatePickupId);
    }
  }, [duplicatePickupId]);

  const fetchExistingPickup = async (pickupId) => {
    setIsLoading(true);
    await handleApiRequest({
      requestFn: () => api.get(`/manager/pickups/${pickupId}`),
      onSuccess: (res) => {
        const existingPickup = res.data;

        setUser(existingPickup.user);
        const mappedRoutes = existingPickup.origins.map((origin, index) => ({
          id: index + 1,
          origin: {
            address: origin.address || '',
            latitude: origin.latitude || '',
            longitude: origin.longitude || '',
            contact_name: origin.contact_name || '',
            contact_phone: origin.contact_phone || '',
          },
          dropoff: existingPickup.dropoffs[index]
            ? {
                address: existingPickup.dropoffs[index].address || '',
                latitude: existingPickup.dropoffs[index].latitude || '',
                longitude: existingPickup.dropoffs[index].longitude || '',
                contact_name: existingPickup.dropoffs[index].contact_name || '',
                contact_phone:
                  existingPickup.dropoffs[index].contact_phone || '',
              }
            : {},
          originUseMyself: false,
          dropoffUseMyself: false,
        }));
        setRoutes(mappedRoutes);
        setItems(
          existingPickup.items.map((item, index) => ({
            id: index + 1,
            item_name: item.item_name,
            weight: item.weight,
            size: item.size,
            details: item.details,
            routeId: index + 1,
          }))
        );
        setPickup({
          origins: existingPickup.origins,
          dropoffs: existingPickup.dropoffs,
        });
        setTruckType(existingPickup.truck_type.id);
        setCargoInsurance(existingPickup.cargo_insurance.id);
        setAdditionalInfo(existingPickup.additional_info || '');
        setIsScheduled(!!existingPickup.scheduled_pickup_time);

        // Check if scheduled pickup time is in the past
        let scheduledTime = existingPickup.scheduled_pickup_time
          ? new Date(existingPickup.scheduled_pickup_time)
          : null;
        const now = new Date();

        if (scheduledTime && scheduledTime < now) {
          scheduledTime = new Date(now.getTime() + 24 * 60 * 60 * 1000);
        }

        setScheduledPickupTime(
          scheduledTime
            ? moment(scheduledTime).format('YYYY-MM-DD HH:mm:ss')
            : ''
        );
      },
      onError: (error) => {
        setEstimateError(error);
        showError(error);
      },
      finallyFn: () => {
        setIsLoading(false);
      },
    });
  };

  useEffect(() => {
    const getUser = async () => {
      await handleApiRequest({
        requestFn: () => api.get(`/manager/users/${userId}`),
        onSuccess: (res) => {
          setUser(res.data);
        },
        onError: (error) => {
          console.error('There was an error fetching the user details!', error);
        },
      });
    };
    const getTruckTypes = async () => {
      await handleApiRequest({
        requestFn: () => api.get('/manager/truck-types'),
        onSuccess: (res) => {
          setTruckTypeList(res.data);
        },
        onError: (error) => {
          console.error('There was an error fetching the truck types!', error);
        },
      });
    };
    getUser();
    getTruckTypes();
  }, [userId]);

  const handlePickupSelect = (id, address, location) => {
    if (!location) return;
    if (!location.lat || !location.lng) return;

    const newRoutes = routes.map((route) =>
      route.id === id
        ? {
            ...route,
            origin: {
              ...route.origin,
              address,
              latitude: location.lat,
              longitude: location.lng,
            },
          }
        : route
    );
    setRoutes(newRoutes);
    setPickup((prev) => ({
      ...prev,
      origins: newRoutes.map((route) => route.origin),
    }));
  };

  const handleDropoffSelect = (id, address, location) => {
    if (!location) return;
    if (!location.lat || !location.lng) return;

    const newRoutes = routes.map((route) =>
      route.id === id
        ? {
            ...route,
            dropoff: {
              ...route.dropoff,
              address,
              latitude: location.lat,
              longitude: location.lng,
            },
          }
        : route
    );
    setRoutes(newRoutes);
    setPickup((prev) => ({
      ...prev,
      dropoffs: newRoutes.map((route) => route.dropoff),
    }));
  };

  const handleAddRoute = () => {
    const newRouteId = routes.length > 0 ? routes[routes.length - 1].id + 1 : 1;
    setRoutes([
      ...routes,
      {
        id: newRouteId,
        origin: {},
        dropoff: {},
        originUseMyself: false,
        dropoffUseMyself: false,
      },
    ]);
  };

  const handleRemoveRoute = (id) => {
    const newRoutes = routes.filter((route) => route.id !== id);
    setRoutes(newRoutes);

    // Remove items assigned to the removed route
    const newItems = items.filter((item) => item.routeId !== id);

    setItems(newItems);
    setPickup((prev) => ({
      ...prev,
      origins: newRoutes.map((route) => route.origin),
      dropoffs: newRoutes.map((route) => route.dropoff),
    }));
  };

  const handleRouteChange = (id, field, value, isOrigin = true) => {
    const newRoutes = routes.map((route) =>
      route.id === id
        ? {
            ...route,
            [isOrigin ? 'origin' : 'dropoff']: {
              ...route[isOrigin ? 'origin' : 'dropoff'],
              [field]: value,
            },
          }
        : route
    );
    setRoutes(newRoutes);
    setPickup((prev) => ({
      ...prev,
      origins: newRoutes.map((route) => route.origin),
      dropoffs: newRoutes.map((route) => route.dropoff),
    }));
  };

  const handleWaypointsOptimized = (waypoints) => {
    setOptimizedWaypoints(waypoints);
  };

  useEffect(() => {
    if (routes.length > 0 && items.length === 0) {
      setItems([
        {
          id: itemIdCounter.current++,
          item_name: '',
          weight: '0-200Lbs',
          size: 'Small',
          details: '',
          routeId: routes[0].id,
          photo: null,
        },
      ]);
    }
  }, [routes]);

  const handleAddItem = () => {
    if (routes.length > 0) {
      setItems([
        ...items,
        {
          id: itemIdCounter.current++,
          item_name: '',
          weight: '0-200Lbs',
          size: 'Small',
          details: '',
          routeId: routes[0].id,
          photo: null,
        },
      ]);
    } else {
      alert('Please add a route before adding items.');
    }
  };

  const handleUseMyselfChange = (id, isOrigin, useMyself) => {
    setRoutes((prevRoutes) =>
      prevRoutes.map((route) => {
        if (route.id === id) {
          const updatedRoute = { ...route };
          if (isOrigin) {
            updatedRoute.originUseMyself = useMyself;
            if (useMyself) {
              updatedRoute.origin.contact_name = `${user.first_name} ${user.last_name}`;
              updatedRoute.origin.contact_phone = user.phone_num;
            } else {
              updatedRoute.origin.contact_name = '';
              updatedRoute.origin.contact_phone = '';
            }
          } else {
            updatedRoute.dropoffUseMyself = useMyself;
            if (useMyself) {
              updatedRoute.dropoff.contact_name = `${user.first_name} ${user.last_name}`;
              updatedRoute.dropoff.contact_phone = user.phone_num;
            } else {
              updatedRoute.dropoff.contact_name = '';
              updatedRoute.dropoff.contact_phone = '';
            }
          }
          return updatedRoute;
        }
        return route;
      })
    );
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [additionalInfo]);
  const adjustTextareaHeight = () => {
    if (additionalInfoRef.current) {
      additionalInfoRef.current.style.height = 'auto';
      additionalInfoRef.current.style.height = `${additionalInfoRef.current.scrollHeight}px`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isScheduled && !scheduledPickupTime) {
      setError('Please select a scheduled pickup time.');
      return;
    }
    if (isScheduled && new Date(scheduledPickupTime) < new Date()) {
      setError('Scheduled pickup time cannot be in the past.');
      return;
    }

    // Prepare items with routes
    const itemsWithRoutes = items
      .map((item) => {
        const route = routes.find((r) => r.id === item.routeId);
        if (!route) {
          return null;
        }
        return {
          item_name: item.item_name,
          size: item.size,
          weight: item.weight,
          details: item.details,
          origin: {
            latitude: route.origin.latitude,
            longitude: route.origin.longitude,
            address: route.origin.address,
            contact_name: route.origin.contact_name,
            contact_phone: route.origin.contact_phone,
          },
          dropoff: {
            latitude: route.dropoff.latitude,
            longitude: route.dropoff.longitude,
            address: route.dropoff.address,
            contact_name: route.dropoff.contact_name,
            contact_phone: route.dropoff.contact_phone,
          },
          photo: item.photo,
          photoUrl: item.photoUrl,
        };
      })
      .filter(Boolean);

    const formData = new FormData();

    formData.append('add_info', additionalInfo || '');
    formData.append('distance', distance);
    formData.append('origin_lat', pickup.origins[0]?.latitude || '');
    formData.append('origin_long', pickup.origins[0]?.longitude || '');
    formData.append('origin_address', pickup.origins[0]?.address || '');
    formData.append(
      'dropoff_lat',
      pickup.dropoffs[pickup.dropoffs.length - 1]?.latitude || ''
    );
    formData.append(
      'dropoff_long',
      pickup.dropoffs[pickup.dropoffs.length - 1]?.longitude || ''
    );
    formData.append(
      'dropoff_address',
      pickup.dropoffs[pickup.dropoffs.length - 1]?.address || ''
    );
    formData.append('truck_type_id', truckType);
    formData.append('cargo_insurance_id', cargoInsurance);
    if (isScheduled && scheduledPickupTime)
      formData.append('scheduled_pickup_time', scheduledPickupTime);

    itemsWithRoutes.forEach((item, index) => {
      formData.append(`items[${index}][item_name]`, item.item_name);
      formData.append(`items[${index}][weight]`, item.weight);
      formData.append(`items[${index}][size]`, item.size);
      formData.append(`items[${index}][details]`, item.details || '');

      formData.append(
        `items[${index}][origin][address]`,
        item.origin.address || ''
      );
      formData.append(
        `items[${index}][origin][latitude]`,
        item.origin.latitude || ''
      );
      formData.append(
        `items[${index}][origin][longitude]`,
        item.origin.longitude || ''
      );
      formData.append(
        `items[${index}][origin][contact_name]`,
        item.origin.contact_name || ''
      );
      formData.append(
        `items[${index}][origin][contact_phone]`,
        item.origin.contact_phone || ''
      );

      formData.append(
        `items[${index}][dropoff][address]`,
        item.dropoff.address || ''
      );
      formData.append(
        `items[${index}][dropoff][latitude]`,
        item.dropoff.latitude || ''
      );
      formData.append(
        `items[${index}][dropoff][longitude]`,
        item.dropoff.longitude || ''
      );
      formData.append(
        `items[${index}][dropoff][contact_name]`,
        item.dropoff.contact_name || ''
      );
      formData.append(
        `items[${index}][dropoff][contact_phone]`,
        item.dropoff.contact_phone || ''
      );

      if (item.photo && item.photo instanceof File) {
        formData.append(`items[${index}][photo]`, item.photo);
      } else if (item.photoUrl) {
        formData.append(`items[${index}][photo_url]`, item.photoUrl);
      }
    });

    pickup.origins.forEach((origin, index) => {
      formData.append(`origins[${index}][address]`, origin.address || '');
      formData.append(`origins[${index}][latitude]`, origin.latitude || '');
      formData.append(`origins[${index}][longitude]`, origin.longitude || '');
      formData.append(
        `origins[${index}][contact_name]`,
        origin.contact_name || ''
      );
      formData.append(
        `origins[${index}][contact_phone]`,
        origin.contact_phone || ''
      );
    });

    pickup.dropoffs.forEach((dropoff, index) => {
      formData.append(`dropoffs[${index}][address]`, dropoff.address || '');
      formData.append(`dropoffs[${index}][latitude]`, dropoff.latitude || '');
      formData.append(`dropoffs[${index}][longitude]`, dropoff.longitude || '');
      formData.append(
        `dropoffs[${index}][contact_name]`,
        dropoff.contact_name || ''
      );
      formData.append(
        `dropoffs[${index}][contact_phone]`,
        dropoff.contact_phone || ''
      );
    });

    if (optimizedWaypoints && optimizedWaypoints.length > 0) {
      optimizedWaypoints.forEach((waypoint, index) => {
        formData.append(
          `optimizedWaypoints[${index}][location][lat]`,
          waypoint.location.lat()
        );
        formData.append(
          `optimizedWaypoints[${index}][location][lng]`,
          waypoint.location.lng()
        );
      });
    }

    setError('');
    setIsSubmitted(true);

    await handleApiRequest({
      requestFn: () => api.post(`/manager/pickups/create/${userId}`, formData),
      onSuccess: (res) => {
        showSuccess('Pickup created successfully');
        navigate(`/pickups/${res.pickup_id}`);
      },
      onError: (error) => {
        setError(error);
        showError(error);
      },
      finallyFn: () => {
        setIsSubmitted(false);
      },
    });
  };

  const lastDropoff = pickup.dropoffs[pickup.dropoffs.length - 1];
  const lastDropoffLat = lastDropoff?.latitude;
  const lastDropoffLng = lastDropoff?.longitude;

  useEffect(() => {
    if (
      distance > 0 &&
      truckType &&
      cargoInsurance &&
      lastDropoffLat &&
      lastDropoffLng
    ) {
      const requestData = {
        distance,
        truck_type_id: truckType,
        cargo_insurance_id: cargoInsurance,
        dropoff_lat: lastDropoffLat,
        dropoff_long: lastDropoffLng,
      };
      if (isScheduled && scheduledPickupTime)
        requestData.scheduled_pickup_time = scheduledPickupTime;

      setIsEstimating(true);
      setEstimateError('');
      const fetchPriceEstimate = async () => {
        await handleApiRequest({
          requestFn: () =>
            api.post(`/manager/pickups/price-preview`, requestData),
          onSuccess: (res) => {
            setPriceEstimate(res);
          },
          onError: (error) => {
            setEstimateError(error);
            showError(error);
          },
          finallyFn: () => {
            setIsEstimating(false);
          },
        });
      };
      fetchPriceEstimate();
    } else {
      setPriceEstimate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    distance,
    truckType,
    cargoInsurance,
    lastDropoff?.latitude,
    lastDropoff?.longitude,
    isScheduled,
    scheduledPickupTime,
  ]);

  return (
    <GoogleMapsProvider>
      <div className="container-fluid mt-5">
        <h2 className="text-center">
          {duplicatePickupId ? (
            <>
              Duplicate{' '}
              <Link to={`/pickups/${duplicatePickupId}`}>
                #{duplicatePickupId}
              </Link>
            </>
          ) : (
            'Create '
          )}{' '}
          Pickup for
          <Link to={`/users/${userId}`}>
            {' '}
            #{userId}{' '}
            {user
              ? user.company
                ? user.company.business_name
                : user.full_name
              : ''}
          </Link>
        </h2>
        {error && <div className="alert alert-danger">{error}</div>}
        {isLoading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="row">
            <div className="col-xl-6">
              <div className="card">
                <div className="card-body">
                  <form
                    onSubmit={handleSubmit}
                    onKeyDown={(e) => {
                      if (
                        e.key === 'Enter' &&
                        e.target.tagName !== 'TEXTAREA'
                      ) {
                        e.preventDefault();
                      }
                    }}
                  >
                    {routes.map((route, index) => (
                      <RouteSection
                        key={route.id}
                        index={index}
                        routes={routes}
                        route={route}
                        user={user}
                        handlePickupSelect={handlePickupSelect}
                        handleDropoffSelect={handleDropoffSelect}
                        handleUseMyselfChange={handleUseMyselfChange}
                        handleRouteChange={handleRouteChange}
                        handleRemoveRoute={handleRemoveRoute}
                        handleAddRoute={handleAddRoute}
                      />
                    ))}
                    <button
                      type="button"
                      className="btn btn-secondary mb-2"
                      onClick={handleAddRoute}
                    >
                      Add Route
                    </button>
                    <DeliveryOptions
                      truckType={truckType}
                      setTruckType={setTruckType}
                      truckTypeList={truckTypeList}
                      cargoInsurance={cargoInsurance}
                      setCargoInsurance={setCargoInsurance}
                      additionalInfo={additionalInfo}
                      setAdditionalInfo={setAdditionalInfo}
                      additionalInfoRef={additionalInfoRef}
                      setIsScheduled={setIsScheduled}
                      isScheduled={isScheduled}
                      scheduledPickupTime={scheduledPickupTime}
                      setScheduledPickupTime={setScheduledPickupTime}
                    />
                    <hr />
                    {/* ITEMS */}
                    <h5 className="text-center">Items</h5>
                    <ItemSection
                      items={items}
                      setItems={setItems}
                      routes={routes}
                      itemDetailsRefs={itemDetailsRefs}
                    />

                    <button
                      type="button"
                      className="btn btn-warning mb-2"
                      onClick={handleAddItem}
                    >
                      Add Item
                    </button>
                    <hr />
                    <div className="d-flex justify-content-center">
                      <button className="btn btn-primary">
                        {isSubmitted ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                            &nbsp;Creating...
                          </>
                        ) : (
                          'Create'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="col-xl-6">
              <div className="card">
                <div className="card-body" style={{ minHeight: '400px' }}>
                  <NPMapViewDirections
                    origins={pickup.origins}
                    dropoffs={pickup.dropoffs}
                    onDistanceCalculated={setDistance}
                    onWaypointsOptimized={handleWaypointsOptimized}
                  />
                  <div className="mt-2">Distance: {distance} km</div>
                  <PriceEstimate
                    priceEstimate={priceEstimate}
                    estimateError={estimateError}
                    isEstimating={isEstimating}
                    truckType={truckType}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </GoogleMapsProvider>
  );
};

export default withAuth(PickupCreate);
